// @flow

import { disbatch } from 'redux-act';

import { getCart } from '@gandi/cart';
import { useTranslator, type TranslatorProps } from '@gandi/translate';

import type { AvailabilityIconName } from 'src/Domain/AvailabilityIcon';
import { useAppSelector } from 'src/hooks';
import type { ThunkAction } from 'src/types';

import {
  addRenewableAndServices,
  setCartProductState,
} from '../../modules/actions';
import {
  selectExtendedResults,
  selectRenewableResultsToAdd,
} from '../../modules/selectors';

import type { ExtendedResult } from '../../types';

export const RENEWABLE_LIMIT = 20 as const;

export type DomainRenewPossibility =
  | 'creatable'
  | 'notAtGandi'
  | 'unavailable'
  | 'transferable'
  | 'renewable'
  | 'restorable'
  | 'invalid'
  | 'error'
  | 'loading';

export const getDomainPossibility = ({
  extraneous,
  restorable,
  renewable,
  availability,
  loading,
  transferable,
  invalid,
}: ExtendedResult): DomainRenewPossibility => {
  if (restorable) return 'restorable';

  if (renewable) return 'renewable';

  if (transferable) return 'transferable';

  if (invalid) return 'invalid';

  if (loading) return 'loading';

  if (extraneous && availability === 'unavailable') return 'notAtGandi';

  if (availability === 'unavailable') return 'unavailable';

  if (extraneous && availability === 'available') return 'creatable';

  return 'error';
};

export const renewPossibility2AvailabilityIconName: Record<
  DomainRenewPossibility,
  AvailabilityIconName,
> = {
  creatable: 'ok',
  notAtGandi: 'fail',
  unavailable: 'fail',
  transferable: 'fail',
  renewable: 'ok',
  restorable: 'ok',
  invalid: 'fail',
  error: 'fail',
  loading: 'warning',
};

export const useAvailabilityText = ({
  domain,
}: {
  domain: ExtendedResult,
}): string => {
  const { __ } = useTranslator();

  const possibility = getDomainPossibility(domain);

  if (possibility === 'creatable')
    return __('This domain name is available for registration!');

  if (possibility === 'notAtGandi')
    return __('This domain is not registred with Gandi.');

  if (possibility === 'unavailable') return __('This domain is not renewable.');

  if (possibility === 'transferable')
    return __('This domain is already registered. Is it yours? Transfer it!');

  if (possibility === 'restorable')
    return __('This domain is available for restoration!');

  if (possibility === 'renewable')
    return __('This domain is available for renew!');

  if (possibility === 'invalid') return __('This is not a valid domain name.');

  if (possibility === 'error') return __('Something seems broken right now.');

  // it should be a loading state
  return '';
};

type SortedResults = Record<DomainRenewPossibility, Array<ExtendedResult>>;
type CanAddResultsPossibilities = 'renewable' | 'restorable';

export type GroupedResults = {
  canAddToCartResults: Record<
    CanAddResultsPossibilities,
    Array<ExtendedResult>,
  >,
  otherResults: Record<
    Exclude<DomainRenewPossibility, CanAddResultsPossibilities>,
    Array<ExtendedResult>,
  >,
};

export const useSortResultsByPossibility = (): GroupedResults => {
  const results: SortedResults = {
    renewable: [],
    restorable: [],
    creatable: [],
    loading: [],
    notAtGandi: [],
    unavailable: [],
    transferable: [],
    invalid: [],
    error: [],
  };

  const allResults = useAppSelector(selectExtendedResults);

  allResults.forEach((result: ExtendedResult) => {
    const possibility = getDomainPossibility(result);
    results[possibility].push(result);
  });

  const { renewable, restorable, ...otherResults } = results;
  const canAddToCartResults = { renewable, restorable };
  return {
    canAddToCartResults,
    otherResults,
  };
};

export const addAllToCart =
  (translator: TranslatorProps): ThunkAction<Promise<any>> =>
  async (dispatch, getState) => {
    const resultsToAdd = selectRenewableResultsToAdd(getState());

    try {
      disbatch(
        dispatch,
        resultsToAdd.map(({ fqdn }) =>
          setCartProductState({ fqdn, cartProductState: 'adding' }),
        ),
      );
      await dispatch(addRenewableAndServices(resultsToAdd, translator));
    } finally {
      disbatch(
        dispatch,
        resultsToAdd.map(({ fqdn }) =>
          setCartProductState({ fqdn, cartProductState: 'none' }),
        ),
      );
    }

    return dispatch(getCart());
  };

const NULL_EXPIRED_DATA_INFO = {
  expireDate: '',
  expireTheme: '',
  isExpired: false,
};

export const useExpireDataInfo: (date: string) => {
  expireDate: string,
  expireTheme: string,
  isExpired: boolean,
} = date => {
  const translator = useTranslator();
  const { __ } = translator;

  if (!date) return NULL_EXPIRED_DATA_INFO;

  const endDate = translator.localize(date);
  const diffHours = endDate.diff(Date.now(), 'hours');
  const diffDays = endDate.diff(Date.now(), 'days');
  const expirationDate = translator.formatDate(endDate, 'll');

  let expireDate = __('Expires %(expirationDate)s', { expirationDate });
  let expireTheme = 'warning';
  let isExpired = false;

  if (diffHours < 0) {
    expireDate = __('Expired on %(expirationDate)s', { expirationDate });
    expireTheme = 'danger';
    isExpired = true;
  } else if (diffDays < 15) {
    expireDate = __('Expires in %(diffDays)s days', { diffDays });
    expireTheme = 'danger';
  }

  return { expireDate, expireTheme, isExpired };
};
